import { LDateTime } from '@app/components/date/LDateTime';
import { UserLabel } from '@app/components/users/UserLabel';
import { socialPlatforms } from '@app/modules/social-export/domain/platforms';
import { webApi } from '@mi-front/api-clients';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MovieIcon from '@mui/icons-material/Movie';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  Chip,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ReactNode } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { SocialExportUploadStatusChip } from './SocialExportUploadStatusChip';
import { socialExportPublishCardMessages } from './social-export-publish-messages';
import { SocialExportUploadCardDto } from './useSocialExportUploadCardActions';

export interface SocialExportUploadItemCardProps {
  upload: SocialExportUploadCardDto;
  actionsTrigger: ReactNode;
  actionsMenuAndDialog: ReactNode;
}

function getEditForChip(upload: SocialExportUploadCardDto): { name: string } | null {
  if ('edit' in upload && upload.edit) {
    return upload.edit;
  }
  return null;
}

/**
 * Shared social upload card layout (fragment + composition BFF upload rows).
 */
export function SocialExportUploadItemCard({
  upload,
  actionsTrigger,
  actionsMenuAndDialog,
}: SocialExportUploadItemCardProps) {
  const { $t } = useIntl();
  const platform = socialPlatforms.get(upload.platform);
  const edit = getEditForChip(upload);

  const isCompleted = upload.status === 'COMPLETED';
  const completedData = upload.data as webApi.UploadCompletedDataDto | undefined;
  const scheduledData = upload.data as webApi.UploadScheduledDataDto | undefined;

  const dateValue = isCompleted
    ? completedData?.publishedAt
    : upload.status === 'SCHEDULED'
      ? scheduledData?.publishAt
      : upload.createdAt;

  const hasPublicUrl = Boolean(upload.platformFeatures?.publicUrl);
  const viewVideoButton = isCompleted ? (
    hasPublicUrl ? (
      <Button
        component="a"
        href={upload.platformFeatures!.publicUrl}
        target="_blank"
        rel="noreferrer"
        startIcon={<OpenInNewIcon />}
      >
        <FormattedMessage {...socialExportPublishCardMessages.viewVideo} />
      </Button>
    ) : (
      <Tooltip title={$t(socialExportPublishCardMessages.waitingPublicLink)} placement="top">
        <Box component="span" display="inline-flex">
          <Button disabled startIcon={<AccessTimeIcon fontSize="small" />}>
            <FormattedMessage {...socialExportPublishCardMessages.viewVideo} />
          </Button>
        </Box>
      </Tooltip>
    )
  ) : null;

  const shouldRenderActionsGroup = Boolean(viewVideoButton) || Boolean(actionsTrigger);

  return (
    <Card sx={{ overflow: 'visible' }}>
      <CardContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" alignItems="center" spacing={2} flex={1} minWidth={0}>
            <Stack spacing={0.5} minWidth={0}>
              <Stack direction="row" alignItems="center" gap={1} mb={1}>
                {platform.Icon({ sx: { fontSize: 24 }, colored: true })}
                <Typography variant="subtitle1" fontWeight={500}>
                  {platform.name}
                </Typography>
              </Stack>
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <SocialExportUploadStatusChip upload={upload} />
                {edit ? (
                  <Chip
                    sx={{ pl: 0.5 }}
                    icon={<MovieIcon fontSize="small" color="secondary" />}
                    label={
                      <FormattedMessage
                        {...socialExportPublishCardMessages.editionWithName}
                        values={{ name: edit.name }}
                      />
                    }
                    size="small"
                    variant="outlined"
                  />
                ) : null}
              </Box>
              <Stack direction="row" alignItems="center" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
                <Typography component="div" variant="body2" color="text.secondary">
                  <FormattedMessage
                    {...socialExportPublishCardMessages.byUser}
                    values={{
                      user: <UserLabel user={upload.creator} />,
                    }}
                  />
                  {' • '}
                  <LDateTime value={dateValue ?? upload.createdAt} showRelative="inline" />
                </Typography>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction="row" alignItems="center" flexShrink={0}>
            {shouldRenderActionsGroup ? (
              <ButtonGroup variant="outlined" size="small">
                {viewVideoButton}
                {actionsTrigger}
              </ButtonGroup>
            ) : null}
            {actionsMenuAndDialog}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
